import {
  collection, doc, onSnapshot, setDoc, Timestamp,
  type DocumentData, type Firestore, type Unsubscribe,
} from 'firebase/firestore';
import { doc as restDoc, getDoc as restGetDoc, type Firestore as RestFirestore } from 'firebase/firestore/lite';
import { relayIceConfiguration, type OutputViewGrant } from './outputViewPeer';
import {
  encodeRoomLinkMessage, ROOM_LINK_PROTOCOL, ROOM_LINK_STATE_MESSAGES, RoomLinkChunkAssembler,
  type RoomLinkOutbound,
} from './roomLinkCodec';
import type { RoomLinkCall, RoomLinkTransport, RoomLinkTransportEvents, RoomLinkTransportGrant } from './roomLink';

/** The `openRoomLink` reply (S6): the link record's id, its ticket, and the desktop side's relay credentials. */
interface RoomLinkGrantReply {
  linkId: string;
  ticket: string;
  expiresAt: number;
  signalingPath: string;
  iceServers: OutputViewGrant['iceServers'];
}

export interface WebRtcTurnOptions {
  db: Firestore;
  restDb: RestFirestore;
  call: RoomLinkCall;
  venueId: string;
  roomId: string;
  /** Which client opens the link (S6/S28): a console tab, wireview, or the gateway's MCP door. Absent means console. */
  client?: 'console' | 'wireview' | 'mcp';
  peerFactory: () => RTCPeerConnection;
}

/** S12: the three channels and their settings; anything else is not this protocol. */
const CHANNELS = {
  control: { label: 'room-link-control', init: { ordered: true, protocol: ROOM_LINK_PROTOCOL } },
  state: { label: 'room-link-state', init: { ordered: true, protocol: ROOM_LINK_PROTOCOL } },
  frames: { label: 'room-link-frames', init: { ordered: false, protocol: ROOM_LINK_PROTOCOL } },
} as const;
/** How often the answer is read over REST while it has not arrived (H49: a listen connection can be held 7–25 s). */
const ANSWER_READ_MS = 250;
/** The selected pair is checked this often; one still unknown this long after connecting is not a relay. */
const PAIR_CHECK_MS = 5_000;
/** A candidates collection holds at most this many entries per side (S4). */
const MAX_CANDIDATES = 63;

function isGrant(value: unknown): value is RoomLinkGrantReply {
  const grant = value as Partial<RoomLinkGrantReply> | null;
  return Boolean(grant) && typeof grant!.linkId === 'string' && grant!.linkId.length > 0 && typeof grant!.ticket === 'string'
    && Number.isSafeInteger(grant!.expiresAt) && typeof grant!.signalingPath === 'string' && grant!.signalingPath.length > 0
    && Array.isArray(grant!.iceServers);
}

/**
 * The `webrtc-turn` transport of the room link (D5, D15, S6, S11, S12): three data channels on one relay-only
 * peer connection, signaled through the link record's Firestore documents.
 *
 * The open is event-driven end to end (D15): the offer is created before the grant and travels inside it, so
 * the controller can answer the moment its one transport read returns. The relay configuration is set after
 * the grant and before the local description, so gathering starts on relay credentials only (Rule 2).
 */
export class WebRtcTurnTransport implements RoomLinkTransport {
  private pc?: RTCPeerConnection;
  private readonly channels: Partial<Record<keyof typeof CHANNELS, RTCDataChannel>> = {};
  private readonly assemblers = { control: new RoomLinkChunkAssembler(), state: new RoomLinkChunkAssembler() };
  private readonly unsubscribers: Unsubscribe[] = [];
  private readonly candidateQueue: RTCIceCandidateInit[] = [];
  private grant?: RoomLinkGrantReply;
  private chunkId = 0;
  private candidateCount = 0;
  private openChannels = 0;
  private answerTaken = false;
  private answerReading = false;
  private answerTimer?: ReturnType<typeof setInterval>;
  private pairTimer?: ReturnType<typeof setInterval>;
  private pairUnknownSince?: number;
  private closed = false;

  constructor(private readonly options: WebRtcTurnOptions, private readonly events: RoomLinkTransportEvents) {}

  async connect(clientPublicKey: string): Promise<RoomLinkTransportGrant> {
    const pc = this.options.peerFactory();
    this.pc = pc;
    for (const name of Object.keys(CHANNELS) as Array<keyof typeof CHANNELS>) {
      const channel = pc.createDataChannel(CHANNELS[name].label, { ...CHANNELS[name].init });
      this.channels[name] = channel;
      channel.onopen = () => this.channelOpened();
      channel.onclose = () => this.end('transport_closed');
      if (name === 'frames') {
        channel.binaryType = 'arraybuffer';
        channel.onmessage = (event) => { if (!this.closed) this.events.onFrame(event.data); };
      } else {
        channel.onmessage = (event) => this.textMessage(name, event.data);
      }
    }
    pc.onicecandidate = (event) => {
      if (event.candidate && !this.closed) void this.sendCandidate(event.candidate);
    };
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed') this.end('transport_closed');
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') { this.end('transport_closed'); return; }
      if (pc.connectionState === 'connected') {
        this.events.onPhase('connected');
        void this.checkPair();
      }
    };
    const offer = await pc.createOffer();
    if (this.closed) throw new Error('room link: closed while opening');
    if (!offer.sdp) throw new Error('room link: the browser produced no offer');
    const reply = await this.options.call<unknown>('openRoomLink', {
      venueId: this.options.venueId, roomId: this.options.roomId, client: this.options.client ?? 'console',
      clientEphemeralPublicKey: clientPublicKey, offerSdp: offer.sdp,
    });
    if (!isGrant(reply)) throw new Error('room link: the grant is malformed');
    if (this.closed) throw new Error('room link: closed while opening');
    this.grant = reply;
    this.events.onPhase('grant');
    pc.setConfiguration(relayIceConfiguration(reply.iceServers));
    await pc.setLocalDescription(offer);
    if (this.closed) throw new Error('room link: closed while opening');
    this.listen(reply.signalingPath);
    this.pairTimer = setInterval(() => { void this.checkPair(); }, PAIR_CHECK_MS);
    return { linkId: reply.linkId, ticket: reply.ticket };
  }

  send(message: RoomLinkOutbound): void {
    if (this.closed) return;
    const channel = ROOM_LINK_STATE_MESSAGES.has(message.type) ? this.channels.state : this.channels.control;
    if (channel?.readyState !== 'open') return;
    for (const text of encodeRoomLinkMessage(message, () => `c${++this.chunkId}`)) channel.send(text);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.teardown();
  }

  private end(reason: 'transport_closed' | 'transport_policy'): void {
    if (this.closed) return;
    this.closed = true;
    this.teardown();
    this.events.onClosed(reason);
  }

  private teardown(): void {
    for (const unsubscribe of this.unsubscribers.splice(0)) unsubscribe();
    this.stopAnswerReads();
    if (this.pairTimer) clearInterval(this.pairTimer);
    this.pairTimer = undefined;
    for (const channel of Object.values(this.channels)) channel.close();
    this.pc?.close();
  }

  private channelOpened(): void {
    this.openChannels += 1;
    if (this.openChannels !== Object.keys(CHANNELS).length || this.closed) return;
    this.events.onPhase('channelOpen');
    this.events.onReady();
  }

  private textMessage(name: 'control' | 'state', raw: unknown): void {
    if (this.closed) return;
    if (typeof raw !== 'string') { this.end('transport_closed'); return; }
    let message;
    try { message = this.assemblers[name].add(raw); } catch { this.end('transport_closed'); return; }
    if (message) this.events.onMessage(message);
  }

  private listen(base: string): void {
    this.unsubscribers.push(onSnapshot(doc(this.options.db, `${base}/signaling/answer`),
      (snapshot) => this.takeAnswer(snapshot.data(), 'listener'), () => this.end('transport_closed')));
    this.unsubscribers.push(onSnapshot(collection(this.options.db, `${base}/candidates/device/entries`), (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type !== 'added' || this.closed) continue;
        const candidate = change.doc.data() as RTCIceCandidateInit;
        if (typeof candidate.candidate !== 'string') { this.end('transport_closed'); return; }
        // The controller's end-of-candidates entry (D15) carries an empty candidate: nothing to add.
        if (!candidate.candidate) continue;
        const value: RTCIceCandidateInit = { candidate: candidate.candidate, sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex, usernameFragment: candidate.usernameFragment };
        if (this.pc?.remoteDescription) void this.pc.addIceCandidate(value).catch(() => this.end('transport_closed'));
        else this.candidateQueue.push(value);
      }
    }, () => this.end('transport_closed')));
    if (!this.answerTaken) this.answerTimer = setInterval(() => { void this.readAnswer(); }, ANSWER_READ_MS);
  }

  /** The controller's answer, from whichever path delivers it first (H49); a second delivery changes nothing. */
  private takeAnswer(data: DocumentData | undefined, via: 'listener' | 'read'): void {
    if (data?.type !== 'answer' || this.closed || this.answerTaken || !this.pc) return;
    if (typeof data.sdp !== 'string') { this.end('transport_closed'); return; }
    this.answerTaken = true;
    this.stopAnswerReads();
    this.events.onPhase('answerReceived', via);
    const pc = this.pc;
    void pc.setRemoteDescription({ type: 'answer', sdp: data.sdp })
      .then(async () => { for (const candidate of this.candidateQueue.splice(0)) await pc.addIceCandidate(candidate); })
      .catch(() => this.end('transport_closed'));
  }

  private async readAnswer(): Promise<void> {
    if (this.closed || this.answerTaken || this.answerReading || !this.grant) return;
    this.answerReading = true;
    try {
      const snapshot = await restGetDoc(restDoc(this.options.restDb, `${this.grant.signalingPath}/signaling/answer`));
      this.takeAnswer(snapshot.data(), 'read');
    } catch { /* Tried again on the next tick; the listener and the connect deadline still decide. */ }
    finally { this.answerReading = false; }
  }

  private stopAnswerReads(): void {
    if (this.answerTimer) clearInterval(this.answerTimer);
    this.answerTimer = undefined;
  }

  private async sendCandidate(candidate: RTCIceCandidate): Promise<void> {
    const grant = this.grant;
    if (this.closed || !grant || this.candidateCount >= MAX_CANDIDATES) return;
    const id = String(this.candidateCount++);
    const data = candidate.toJSON();
    try {
      await setDoc(doc(this.options.db, `${grant.signalingPath}/candidates/desktop/entries/${id}`), {
        candidate: data.candidate, sdpMid: data.sdpMid ?? null, sdpMLineIndex: data.sdpMLineIndex ?? null,
        usernameFragment: data.usernameFragment ?? null,
        expiresAt: grant.expiresAt, purgeAt: Timestamp.fromMillis(grant.expiresAt * 1000),
      });
    } catch { this.end('transport_closed'); }
  }

  /** Rule 2: the selected pair is TURN on both ends, or the link ends `transport_policy` (B78's rule). */
  private async checkPair(): Promise<void> {
    const pc = this.pc;
    if (this.closed || !pc || pc.connectionState !== 'connected') return;
    try {
      const rows = new Map<string, Record<string, unknown>>();
      (await pc.getStats()).forEach((value) => rows.set(value.id, value as unknown as Record<string, unknown>));
      const transport = [...rows.values()].find((row) => row.type === 'transport' && typeof row.selectedCandidatePairId === 'string');
      const pair = transport ? rows.get(String(transport.selectedCandidatePairId))
        : [...rows.values()].find((row) => row.type === 'candidate-pair' && (row.selected === true || row.nominated === true));
      const local = pair ? rows.get(String(pair.localCandidateId)) : undefined;
      const remote = pair ? rows.get(String(pair.remoteCandidateId)) : undefined;
      if (!local || !remote) {
        this.pairUnknownSince ??= Date.now();
        if (Date.now() - this.pairUnknownSince >= PAIR_CHECK_MS) this.end('transport_policy');
        return;
      }
      this.pairUnknownSince = undefined;
      if (local.candidateType !== 'relay' || remote.candidateType !== 'relay') this.end('transport_policy');
    } catch { this.end('transport_policy'); }
  }
}
